import React, { useCallback, useEffect, useState } from 'react';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { fetchAdminHealth } from '../../api/adminHealth';

const FALLBACK_HEALTH = {
  overallUp: false,
  checkedAt: null,
  ccxt: {
    name: 'CCXT',
    up: false,
    message: 'Statut non récupéré',
    statusCode: 'n/a',
    latencyMs: 'n/a',
    url: '/api/admin/health/services',
  },
  python: {
    name: 'Python Pattern Engine',
    up: false,
    message: 'Statut non récupéré',
    statusCode: 'n/a',
    latencyMs: 'n/a',
    url: '/api/admin/health/services',
  },
};

function formatHealthError(error) {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (!message) {
    return 'Erreur de chargement';
  }

  if (message.includes('502 Bad Gateway')) {
    return '502 Bad Gateway sur /api/admin/health/services';
  }

  return message;
}

function ServiceCard({ service }) {
  const up = Boolean(service?.up);
  const color = up ? '#1f7a4d' : '#a43e2a';
  const bg = up ? 'rgba(31,122,77,0.10)' : 'rgba(164,62,42,0.10)';
  const Icon = up ? CheckCircleRoundedIcon : ErrorOutlineRoundedIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: bg,
        border: `1px solid ${color}33`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Icon sx={{ color, mt: 0.3 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ color, fontWeight: 800, mb: 0.5 }}>
            {service?.name || 'Service'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#4f646f', mb: 1.5 }}>
            {up ? 'Service joignable et répond correctement.' : 'Service indisponible ou temps de réponse dépassé.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
            <Chip size="small" label={up ? 'UP' : 'DOWN'} sx={{ bgcolor: 'rgba(255,255,255,0.55)', fontWeight: 700 }} />
            <Chip size="small" label={service?.message || 'n/a'} sx={{ bgcolor: 'rgba(255,255,255,0.55)' }} />
            <Chip size="small" label={`Code: ${service?.statusCode ?? 'n/a'}`} sx={{ bgcolor: 'rgba(255,255,255,0.55)' }} />
            <Chip size="small" label={`Latence: ${service?.latencyMs ?? 'n/a'} ms`} sx={{ bgcolor: 'rgba(255,255,255,0.55)' }} />
          </Stack>
          <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mt: 1.5 }}>
            {service?.url}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const HealthAdminPage = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const displayedHealth = health || FALLBACK_HEALTH;

  const loadHealth = useCallback(async (withSpinner = true) => {
    if (withSpinner) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    try {
      const payload = await fetchAdminHealth();
      setHealth(payload);
    } catch (err) {
      setError(formatHealthError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadHealth(true);
    const interval = setInterval(() => loadHealth(false), 15000);
    return () => clearInterval(interval);
  }, [loadHealth]);

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Health" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Vérification des services CCXT et Python
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page interroge le backend Java, qui vérifie lui-même la disponibilité du microservice CCXT et du moteur Python.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                onClick={() => loadHealth(false)}
                disabled={loading || refreshing}
                startIcon={refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshRoundedIcon />}
                sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800, '&:hover': { bgcolor: '#e7b94f' } }}
              >
                Actualiser
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
                État global
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                Dernière vérification: {displayedHealth.checkedAt ? new Date(displayedHealth.checkedAt).toLocaleString('fr-FR') : 'n/a'}
              </Typography>
            </Box>
            <Chip
              icon={Boolean(displayedHealth.overallUp) ? <CheckCircleRoundedIcon /> : <HelpOutlineRoundedIcon />}
              label={Boolean(displayedHealth.overallUp) ? 'Tous les services sont UP' : 'Au moins un service est DOWN'}
              color={Boolean(displayedHealth.overallUp) ? 'success' : 'warning'}
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          {loading && !health ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                Chargement des statuts...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.2}>
              <ServiceCard service={displayedHealth.ccxt} />
              <ServiceCard service={displayedHealth.python} />
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default HealthAdminPage;